import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';

import { skipCommonModules } from '../../utils/amplience/amplience';
import {
  fetchContentMetadata,
  fetchFolderDetails,
  fetchRepoDetails,
  fetchSlotSchedulingDetails,
  fetchUserDetails
} from '../../utils/amplienceUtils/amplienceUtils';

/**
 * Custom hook to fetch Amplience metadata and related details for a given module.
 * @param {Object} module The module object containing id and type.
 * @returns {Object} An object containing loading state, errors, and fetched data.
 */
const useAmplienceData = ( module ) => {
  const [metadata, setMetadata] = useState( null );
  const [history, setHistory] = useState( null );
  const [folder, setFolder] = useState( null );
  const [repo, setRepo] = useState( null );
  const [assignments, setAssignments] = useState( [] );
  const [users, setUsers] = useState( {} );
  const [error, setError] = useState( null );
  const { serverRouteUtility } = useAppConfigContext();

  const isSkipModule = useMemo( () => (
    skipCommonModules.includes( module?.type ) || skipCommonModules.includes( module?.moduleName )
  ), [module?.type, module?.moduleName] );

  const [loadingStates, setLoadingStates] = useState( {
    meta: false,
    user: false,
    repo: false,
    folder: false,
    scheduling: false
  } );

  const isLoading = useMemo( () => Object.values( loadingStates ).some( Boolean ), [loadingStates] );

  const updateLoading = useCallback( ( key, value ) => {
    setLoadingStates( ( prev ) => ( { ...prev, [key]: value } ) );
  }, [] );

  const userRefs = useMemo( () => [
    { type: 'lastModifiedBy', id: metadata?.lastModifiedBy },
    { type: 'createdBy', id: metadata?.createdBy }
  ].filter( ( ref ) => !!ref.id ), [metadata?.lastModifiedBy, metadata?.createdBy] );

  // Reset and fetch metadata when module ID changes
  useEffect( () => {
    let mounted = true;
    setMetadata( null );
    setHistory( null );
    setFolder( null );
    setRepo( null );
    setUsers( {} );
    setAssignments( [] );
    setError( null );

    if( module?.id && !isSkipModule ){
      updateLoading( 'meta', true );

      fetchContentMetadata( module.id, serverRouteUtility )
        .then( ( data ) => {
          if( mounted ){
            setMetadata( data );
            setHistory( {
              date: data.lastModifiedDate,
              editor: data.lastModifiedBy || 'Unknown'
            } );
          }
        } )
        .catch( ( err ) => {
          if( mounted ){
            // eslint-disable-next-line no-console
            console.error( 'Failed to fetch Amplience metadata:', err );
            setError( `Failed to fetch metadata: ${err.message}` );
          }
        } )
        .finally( () => {
          if( mounted ){
            updateLoading( 'meta', false );
          }
        } );
    }
    return () => {
      mounted = false;
    };
  }, [module?.id, isSkipModule, serverRouteUtility, updateLoading] );

  // Fetch Repository details when metadata changes
  useEffect( () => {
    let mounted = true;
    if( metadata?.contentRepositoryId && !isSkipModule ){
      updateLoading( 'repo', true );

      fetchRepoDetails( metadata.contentRepositoryId, serverRouteUtility )
        .then( ( data ) => {
          if( mounted ){
            setRepo( data );
          }
        } )
        .catch( ( err ) => {
          if( mounted ){
            // eslint-disable-next-line no-console
            console.error( 'Failed to fetch Amplience repository details:', err );
          }
        } )
        .finally( () => {
          if( mounted ){
            updateLoading( 'repo', false );
          }
        } );
    }
    return () => {
      mounted = false;
    };
  }, [metadata?.contentRepositoryId, isSkipModule, serverRouteUtility, updateLoading] );

  // Fetch Folder details when metadata changes
  useEffect( () => {
    let mounted = true;
    if( metadata?.folderId && !isSkipModule ){
      updateLoading( 'folder', true );

      fetchFolderDetails( metadata.folderId, serverRouteUtility )
        .then( ( data ) => {
          if( mounted ){
            setFolder( data );
          }
        } )
        .catch( ( err ) => {
          if( mounted ){
            // eslint-disable-next-line no-console
            console.error( 'Failed to fetch Amplience folder details:', err );
          }
        } )
        .finally( () => {
          if( mounted ){
            updateLoading( 'folder', false );
          }
        } );
    }
    return () => {
      mounted = false;
    };
  }, [metadata?.folderId, isSkipModule, serverRouteUtility, updateLoading] );

  // Fetch Scheduling details for slots
  useEffect( () => {
    let mounted = true;
    if( module?.id && module?.type?.toLowerCase() === 'verticalslot' && !isSkipModule ){
      updateLoading( 'scheduling', true );

      fetchSlotSchedulingDetails( module.id, serverRouteUtility )
        .then( ( data ) => {
          if( mounted ){
            setAssignments( data?._embedded?.assignments || [] );
          }
        } )
        .catch( ( err ) => {
          if( mounted ){
            // eslint-disable-next-line no-console
            console.error( 'Failed to fetch Amplience scheduling content:', err );
          }
        } )
        .finally( () => {
          if( mounted ){
            updateLoading( 'scheduling', false );
          }
        } );
    }
    return () => {
      mounted = false;
    };
  }, [module?.id, module?.type, isSkipModule, serverRouteUtility, updateLoading] );

  // Fetch User details (CreatedBy / LastModifiedBy)
  useEffect( () => {
    if( userRefs.length === 0 || isSkipModule ){
      return;
    }

    let mounted = true;
    updateLoading( 'user', true );

    Promise.all(
      userRefs.map( ( ref ) =>
        fetchUserDetails( ref.id, serverRouteUtility )
          .then( ( user ) => ( { type: ref.type, value: user } ) )
          .catch( ( error ) => ( { type: ref.type, value: { error } } ) )
      )
    )
      .then( ( results ) => {
        if( mounted ){
          const usersObj = results.reduce(
            ( acc, { type, value } ) => ( {
              ...acc,
              [type]: value
            } ),
            {}
          );
          setUsers( usersObj );
        }
      } )
      .catch( ( err ) => {
        if( mounted ){
          // eslint-disable-next-line no-console
          console.error( 'Failed to fetch some users:', err );
        }
      } )
      .finally( () => {
        if( mounted ){
          updateLoading( 'user', false );
        }
      } );

    return () => {
      mounted = false;
    };
  }, [userRefs, isSkipModule, serverRouteUtility, updateLoading] );

  return {
    isLoading,
    error,
    metadata,
    history,
    repo,
    folder,
    assignments,
    users
  };
};

export default useAmplienceData;
